import {UIEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {debounce} from 'lodash';

export type PagedSelectParams = {
  Keyword?: string;
  Page: number;
  PageSize: number;
};

export type PagedSelectResponse<T> = {
  Data?: Array<T>;
  Total?: number;
};

type Options<T> = {
  pageSize?: number;
  debounceMs?: number;
  // Always offered (ahead of the loaded pages), e.g. the saved selection of an
  // edited record, so the select never falls back to showing a raw id.
  pinnedItems?: Array<T>;
};

export type PagedSelectOptions<T> = {
  items: Array<T>;
  loading: boolean;
  hasMore: boolean;
  // Loads the next page of the current search, e.g. when the caller hides
  // some items and the dropdown is too short to scroll.
  loadMore: () => void;
  onSearch: (keyword: string) => void;
  onPopupScroll: (event: UIEvent<HTMLDivElement>) => void;
  onDropdownVisibleChange: (open: boolean) => void;
};

// Distance (px) from the bottom of the dropdown that triggers the next page.
const SCROLL_THRESHOLD = 32;

// Server-searched select options that load the next page on scroll until the
// total count is reached.
const usePagedSelectOptions = <T extends {Id: string}>(
  fetchPage: (params: PagedSelectParams) => Promise<PagedSelectResponse<T>>,
  {pageSize = 20, debounceMs = 500, pinnedItems}: Options<T> = {},
): PagedSelectOptions<T> => {
  const [items, setItems] = useState<Array<T>>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [keyword, setKeyword] = useState('');

  // Identifies the latest request so stale responses (older search terms) are ignored.
  const requestIdRef = useRef(0);
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  const loadPage = useCallback(
    async (term: string, pageNumber: number) => {
      const requestId = ++requestIdRef.current;
      try {
        setLoading(true);
        const result = await fetchPageRef.current({
          Keyword: term || undefined,
          Page: pageNumber,
          PageSize: pageSize,
        });
        if (requestId !== requestIdRef.current) return;
        const pageItems = result?.Data ?? [];
        setItems((prev) =>
          pageNumber === 1
            ? pageItems
            : [
                ...prev,
                ...pageItems.filter(
                  (item) => !prev.some((p) => p.Id === item.Id),
                ),
              ],
        );
        setHasMore(
          pageItems.length === pageSize &&
            pageNumber * pageSize < (result?.Total ?? 0),
        );
        setPage(pageNumber);
      } catch (error) {
        if (requestId === requestIdRef.current) setHasMore(false);
        console.error(error);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    loadPage('', 1);
  }, [loadPage]);

  const debouncedSearch = useMemo(
    () => debounce((term: string) => loadPage(term, 1), debounceMs),
    [loadPage, debounceMs],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const onSearch = (value: string) => {
    setKeyword(value);
    debouncedSearch(value);
  };

  const loadMore = () => {
    if (!hasMore || loading) return;
    loadPage(keyword, page + 1);
  };

  const onPopupScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (
      target.scrollTop + target.clientHeight >=
      target.scrollHeight - SCROLL_THRESHOLD
    ) {
      loadMore();
    }
  };

  const onDropdownVisibleChange = (open: boolean) => {
    // Reset to the unfiltered first page when the dropdown closes after a search.
    if (!open && keyword) {
      debouncedSearch.cancel();
      setKeyword('');
      loadPage('', 1);
    }
  };

  const visibleItems = useMemo(() => {
    const missing = (pinnedItems ?? []).filter(
      (pinned) => !items.some((item) => item.Id === pinned.Id),
    );
    return missing.length ? [...missing, ...items] : items;
  }, [items, pinnedItems]);

  return {
    items: visibleItems,
    loading,
    hasMore,
    loadMore,
    onSearch,
    onPopupScroll,
    onDropdownVisibleChange,
  };
};

// Resolves saved ids into full items: starts from the fallback (available
// immediately) and replaces it with the fetched item once it arrives.
export const useSavedSelectItems = <T extends {Id: string}>(
  ids: Array<string> | undefined,
  fetchById: (id: string) => Promise<T>,
  getFallback: (id: string, index: number) => T,
): Array<T> => {
  const idsKey = (ids ?? []).filter(Boolean).join(',');
  const [resolved, setResolved] = useState<Record<string, T>>({});
  const fetchByIdRef = useRef(fetchById);
  fetchByIdRef.current = fetchById;
  const getFallbackRef = useRef(getFallback);
  getFallbackRef.current = getFallback;

  useEffect(() => {
    if (!idsKey) return;
    let cancelled = false;
    const savedIds = idsKey.split(',');
    Promise.all(
      savedIds.map((id) =>
        fetchByIdRef.current(id).catch((error) => {
          console.error(error);
          return undefined;
        }),
      ),
    ).then((results) => {
      if (cancelled) return;
      setResolved((prev) => {
        const next = {...prev};
        results.forEach((item, index) => {
          if (item?.Id) next[savedIds[index]] = item;
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return useMemo(
    () =>
      idsKey
        ? idsKey
            .split(',')
            .map((id, index) => resolved[id] ?? getFallbackRef.current(id, index))
        : [],
    [idsKey, resolved],
  );
};

export default usePagedSelectOptions;
